/**
 * Comment mentions — the one place that knows how a person is named inside a
 * comment body.
 *
 * Stored form (what entity_comments.body holds):      Hello @[Dana Reyes](user:3f2a…)
 * Typed form (what a person sees in the composer):     Hello @Dana Reyes
 * Rendered form (what the Discussion panel shows):     Hello [@Dana Reyes] as a chip
 * Spoken form (notifications, Slack, history rows):    Hello @Dana Reyes
 *
 * The server reads `entity_comments.mentions` (uuid[]), never the body, so the
 * composer sends both: the tokenised body and the ids it contains. Migration
 * 815 keeps the same token shape in `internal_comment_excerpt`.
 */

export interface MentionCandidate {
  /** auth.users id — what goes into entity_comments.mentions */
  id: string;
  /** Display name; falls back to the email's local part when a person has no name yet */
  name: string;
  email?: string;
}

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/** Matches one stored mention token: `@[Name](user:<uuid>)`. */
export const MENTION_TOKEN_PATTERN = new RegExp(`@\\[([^\\]]+)\\]\\(user:(${UUID})\\)`, 'g');

/** A scheme react-markdown passes through untouched, so a token can become a chip. */
export const MENTION_HREF_PREFIX = 'mention:';

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The name a person is shown as in a mention list and a chip. */
export function mentionDisplayName(c: { name?: string | null; email?: string | null }): string {
  const name = (c.name ?? '').trim();
  if (name) return name;
  const email = (c.email ?? '').trim();
  if (email.includes('@')) return email.slice(0, email.indexOf('@'));
  return email || 'Someone';
}

/**
 * Turns typed text into the stored body.
 *
 * Every `@Name` whose name is one of `picked` (people chosen from the
 * autocomplete) becomes a token; longer names are matched first so
 * "@Dana Reyes-Smith" is not cut at "@Dana Reyes". Returns the distinct ids
 * that actually appear, in order of first appearance — a person picked and
 * then deleted from the text is not a mention.
 */
export function serializeMentions(
  text: string,
  picked: MentionCandidate[],
): { body: string; mentions: string[] } {
  const byName = [...picked]
    .filter((p) => p.name.trim().length > 0)
    .sort((a, b) => b.name.length - a.name.length);
  let body = text;
  for (const person of byName) {
    // Already-tokenised occurrences are left alone (the lookbehind excludes "@[").
    const re = new RegExp(`(^|[^\\w\\[])@${escapeRegExp(person.name)}(?![\\w])`, 'g');
    body = body.replace(re, (_m, lead: string) => `${lead}@[${person.name}](user:${person.id})`);
  }
  return { body, mentions: parseMentionIds(body) };
}

/** The ids named in a stored body, distinct, in order of first appearance. */
export function parseMentionIds(body: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of body.matchAll(MENTION_TOKEN_PATTERN)) {
    const id = m[2].toLowerCase();
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/** Stored body → the text a person edits (tokens back to `@Name`). */
export function bodyToEditable(body: string): { text: string; picked: MentionCandidate[] } {
  const picked: MentionCandidate[] = [];
  const seen = new Set<string>();
  const text = body.replace(MENTION_TOKEN_PATTERN, (_m, name: string, id: string) => {
    const key = id.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      picked.push({ id: key, name });
    }
    return `@${name}`;
  });
  return { text, picked };
}

/** Stored body → markdown whose mentions are links on the `mention:` scheme. */
export function bodyToMarkdown(body: string): string {
  return body.replace(
    MENTION_TOKEN_PATTERN,
    (_m, name: string, id: string) => `[@${name}](${MENTION_HREF_PREFIX}${id.toLowerCase()})`,
  );
}

/**
 * Stored body → one plain line, as a feed row or a notification reads it.
 * Mirrors `internal_comment_excerpt` in migration 815.
 */
export function plainExcerpt(body: string, max = 280): string {
  const s = body
    .replace(MENTION_TOKEN_PATTERN, (_m, name: string) => `@${name}`)
    .replace(/(\*\*|__|`)/g, '')
    .replace(/^#+\s*/gm, '')
    .replace(/^\s*[-*]\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * The `@query` a person is typing at the caret, if any.
 * `start` is the index of the `@`, so the caller can replace `@query` with the
 * chosen name. An `@` glued to the previous word (an email address) is not a
 * mention.
 */
export function mentionQueryAtCaret(
  text: string,
  caret: number,
): { query: string; start: number } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && /[\w\]]/.test(before[at - 1])) return null;
  const query = before.slice(at + 1);
  if (/[\n\r]/.test(query)) return null;
  // Two spaces, or one space followed by nothing, ends the mention.
  if (/\s\s/.test(query) || /\s$/.test(query)) return null;
  if (query.length > 40) return null;
  return { query, start: at };
}

/** The people whose name or email matches what has been typed so far. */
export function filterMentionCandidates(
  candidates: MentionCandidate[],
  query: string,
  limit = 6,
): MentionCandidate[] {
  const q = query.trim().toLowerCase();
  const scored = candidates
    .map((c) => {
      const name = c.name.toLowerCase();
      const email = (c.email ?? '').toLowerCase();
      let score = -1;
      if (!q) score = 1;
      else if (name.startsWith(q)) score = 4;
      else if (name.split(/\s+/).some((part) => part.startsWith(q))) score = 3;
      else if (email.startsWith(q)) score = 2;
      else if (name.includes(q) || email.includes(q)) score = 1;
      return { c, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.c.name.localeCompare(b.c.name));
  return scored.slice(0, limit).map((x) => x.c);
}

/**
 * The anchor a page passes in the address, `anchor=attribute:<key>:<label>`,
 * read back. Only attribute anchors exist today (migration 815's CHECK).
 */
export interface CommentAnchor {
  type: 'attribute';
  key: string;
  label?: string;
}

export function anchorToParam(anchor: CommentAnchor): string {
  return [anchor.type, anchor.key, anchor.label ?? ''].map(encodeURIComponent).join(':');
}

export function anchorFromParam(raw: string | null | undefined): CommentAnchor | null {
  if (!raw) return null;
  const parts = raw.split(':').map((p) => {
    try {
      return decodeURIComponent(p);
    } catch {
      return p;
    }
  });
  if (parts[0] !== 'attribute' || !parts[1]) return null;
  return { type: 'attribute', key: parts[1], label: parts[2] || undefined };
}
